import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Cat, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12">
      <Card className="w-full max-w-md p-8 rounded-2xl border-border/70 shadow-sm animate-fade-up">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
            <Cat className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-serif text-xl">AnnieHealth</span>
        </div>

        <h2 className="font-serif text-3xl mb-1">Set a new password</h2>
        <p className="text-muted-foreground text-sm mb-6">Choose a new password for your account.</p>

        {!token ? (
          <div data-testid="reset-missing-token">
            <p className="text-sm text-destructive mb-4">
              This reset link is missing its token. Please use the link from your email, or request a new one.
            </p>
            <Link to="/forgot-password">
              <Button variant="outline" className="rounded-xl">Request a new link</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" data-testid="reset-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="rounded-xl"
                placeholder="At least 6 characters" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type="password" data-testid="reset-confirm-input" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required className="rounded-xl"
                placeholder="Re-enter password" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-xl" data-testid="reset-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reset password
            </Button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-primary hover:underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
